<!-- Under /Videos' Unlocked and Locked buttons (2026-10-24, Seán): one short line on what this
     viewer could unlock, from the chosen filter, whether they're signed in and their Workshop and
     Masterclass access (accessSummary.js, from the fullstack's stats.json: nothing is fixed here). Nothing when
     there's nothing to unlock. The sign-in and registration links are the hashes #sign-in and
     #register (the router sends #register to the fullstack). -->
<template>
  <p v-if="message" class="access-message">
    <template v-if="message.kind === 'signIn'">
      You can unlock {{ getAmount(message.free, "free") }} by
      <RouterLink to="#sign-in">signing in</RouterLink>.
    </template>
    <template v-else-if="message.kind === 'register'">
      <template v-if="message.entitlements">You can access {{ message.entitlements }}.</template>
      You can unlock {{ getAmount(message.restricted, "more") }} by registering for{{
        eventLabel ? " the " : " " }}<RouterLink :to="registerTo">
        VIZBI <i>Online</i>{{ eventLabel && ` ${eventLabel}` }}
      </RouterLink>.
    </template>
    <template v-else-if="message.kind === 'locked'">
      You can unlock these {{ getAmount(message.restricted) }} by registering for{{
        eventLabel ? " the " : " " }}<RouterLink :to="registerTo">
        VIZBI <i>Online</i>{{ eventLabel && ` ${eventLabel}` }}
      </RouterLink>.
    </template>
    <template v-else>
      <template v-if="message.free.count && message.restricted.count">
        You can unlock {{ getAmount(message.free, "free") }} by
        <RouterLink to="#sign-in">signing in</RouterLink>, and a further
        {{ getAmount(message.restricted) }} by registering for
        <RouterLink :to="registerTo">VIZBI <i>Online</i></RouterLink>.
      </template>
      <template v-else-if="message.free.count">
        You can unlock {{ getAmount(message.free, "free") }} by
        <RouterLink to="#sign-in">signing in</RouterLink>.
      </template>
      <template v-else>
        You can unlock {{ getAmount(message.restricted) }} by registering for
        <RouterLink :to="registerTo">VIZBI <i>Online</i></RouterLink>.
      </template>
    </template>
  </p>
</template>

<script setup>
import {getVideoAccessNow} from "@/composables/videoAccessNow";
import {computed} from "vue";
import {RouterLink} from "vue-router";
import {getAmount, getEntitlementText, getLockedSummary} from "@/components/Video/accessSummary";
import useLibraryTotals from "@/composables/useLibraryTotals";
import useUserSafe from "@/composables/useUserSafe";
const props = defineProps({
  selected: {type: String, default: ""} // "unlocked", "locked" or "" (none)
});

const EVENT_LABELS = {workshop: "Workshop", masterclass: "Masterclass", "": ""};
const {isSignedIn, user} = useUserSafe();
const {stats} = useLibraryTotals();
const summary = computed(() => {
  const videoAccess = getVideoAccessNow(user.value?.publicMetadata?.videoAccess);
  return getLockedSummary(stats.value, {videoAccess, isSignedIn: isSignedIn.value});
});
const eventLabel = computed(() => EVENT_LABELS[summary.value?.requiredEvent]);
const registerTo = computed(() => {
  const event = summary.value?.requiredEvent;
  return `#register${event ? `&for=${event}` : ""}&go_back_to=/Videos`;
});

function getUnlockedMessage({free, restricted}) {
  if (!isSignedIn.value) return free.count ? {kind: "signIn", free} : null;
  if (!restricted.count) return null;
  const videoAccess = getVideoAccessNow(user.value?.publicMetadata?.videoAccess);
  const entitlements = getEntitlementText(videoAccess);
  return {kind: "register", restricted, entitlements};
}

function getLockedMessage({free, restricted}) {
  if (isSignedIn.value) return restricted.count ? {kind: "locked", restricted} : null;
  return free.count || restricted.count ? {kind: "signedOut", free, restricted} : null;
}

const message = computed(() => {
  if (!summary.value) return null;
  if (props.selected === "unlocked") return getUnlockedMessage(summary.value);
  return props.selected === "locked" ? getLockedMessage(summary.value) : null;
});
</script>

<style scoped>
.access-message {
  margin: 10px 0 0;
  line-height: 1.4;
}
</style>
